// WebMCP — expose TamerinTECH's key actions to AI agents running in the browser.
// Spec: https://webmachinelearning.github.io/webmcp/
// Feature-detected: a no-op in browsers/agents that don't implement navigator.modelContext.

import { translations } from './i18n/translations'
import { PRODUCTS, getProduct } from './products'

const CALENDLY_URL = 'https://calendly.com/tamerintech/30min'
const CONTACT_EMAIL = 'info@tamerin.tech'
const CONTACT_PHONE = '+49 8026 387 25 98'

// One source of truth: the product registry plus the English copy the page renders.
// Agents get the clean canonical URLs (no tracking parameters).
const listProducts = () =>
  PRODUCTS.map((product) => {
    const copy = translations.en.products.items[product.id]
    return {
      name: copy.name,
      status: copy.status,
      statusKind: product.status,
      description: copy.text,
      ...(product.featured ? { role: 'main product' } : {}),
      ...(product.destinationByLanguage ? { url: product.destinationByLanguage.en } : {}),
      ...(product.pilotByLanguage ? { pilotUrl: product.pilotByLanguage.en } : {}),
      ...(product.playStoreUrl ? { playStoreUrl: product.playStoreUrl } : {}),
      ...(product.concluded ? { concluded: product.concluded } : {}),
    }
  })

const text = (value: unknown) => ({
  content: [{ type: 'text', text: typeof value === 'string' ? value : JSON.stringify(value, null, 2) }],
})

type ToolDefinition = {
  name: string
  description: string
  inputSchema: Record<string, unknown>
  execute: (args: Record<string, unknown>) => unknown
}

const tools: ToolDefinition[] = [
  {
    name: 'get_company_info',
    description: 'Get an overview of TamerinTECH GmbH: what it does, its main product ContextFriend, where it is, and how it works with schools.',
    inputSchema: { type: 'object', properties: {}, additionalProperties: false },
    execute: () =>
      text({
        company: 'TamerinTECH GmbH',
        slogan: 'Machine Learns, You Grow',
        summary:
          'TamerinTECH is an AI product company building learning technology for schools and families. Main product: ContextFriend, a private classroom memory that runs on the school\'s own hardware (prototype; five pilot schools wanted for 2026). MBot, an AI math tutor for grades 5+, is live on the web and Android. Open to technology cooperation and innovation projects with schools, universities and education partners.',
        founded: 'Educational technology work since 2017; TamerinTECH GmbH incorporated in 2021 by Ron Tamerin.',
        location: 'Schliersee, Bavaria, Germany',
        languages: ['English', 'German'],
        certification: 'BSFZ Certified Innovation Competence (2023-2025)',
        website: 'https://www.tamerin.tech',
        mainProduct: translations.en.products.items.contextfriend.text,
        partners: PRODUCTS.flatMap((product) =>
          (translations.en.products.items[product.id].institutions ?? []).map(
            (institution) => `${institution.name} (${product.shortName})`
          )
        ),
      }),
  },
  {
    name: 'list_products',
    description: 'List TamerinTECH products and projects with a short description, the current status of each, and their websites.',
    inputSchema: { type: 'object', properties: {}, additionalProperties: false },
    execute: () => text(listProducts()),
  },
  {
    name: 'get_contact_info',
    description: 'Get TamerinTECH contact details: email, phone, the link to book an intro call, and the ContextFriend pilot application.',
    inputSchema: { type: 'object', properties: {}, additionalProperties: false },
    execute: () =>
      text({
        email: CONTACT_EMAIL,
        phone: CONTACT_PHONE,
        bookingUrl: CALENDLY_URL,
        contextFriendPilotUrl: getProduct('contextfriend').pilotByLanguage?.en,
      }),
  },
  {
    name: 'book_intro_call',
    description:
      "Start booking a free 30-minute intro call with TamerinTECH about ContextFriend, MBot or a cooperation idea. Returns the scheduling URL and, when run in a browser tab, opens it.",
    inputSchema: {
      type: 'object',
      properties: {
        open: { type: 'boolean', description: 'If true, open the booking page in a new tab. Default true.' },
      },
      additionalProperties: false,
    },
    execute: (args) => {
      const shouldOpen = args?.open !== false
      if (shouldOpen && typeof window !== 'undefined') {
        window.open(CALENDLY_URL, '_blank', 'noopener')
      }
      return text({ bookingUrl: CALENDLY_URL, opened: shouldOpen })
    },
  },
  {
    name: 'apply_for_contextfriend_pilot',
    description:
      'Open the ContextFriend pilot application (three questions and an email address) for a school that wants to join the 2026 pilot. Returns the URL and, when run in a browser tab, opens it.',
    inputSchema: {
      type: 'object',
      properties: {
        language: { type: 'string', enum: ['en', 'de'], description: 'Application language. Default en.' },
        open: { type: 'boolean', description: 'If true, open the application in a new tab. Default true.' },
      },
      additionalProperties: false,
    },
    execute: (args) => {
      const language = args?.language === 'de' ? 'de' : 'en'
      const url = getProduct('contextfriend').pilotByLanguage?.[language] ?? ''
      const shouldOpen = args?.open !== false
      if (shouldOpen && url && typeof window !== 'undefined') {
        window.open(url, '_blank', 'noopener')
      }
      return text({ pilotUrl: url, opened: shouldOpen && Boolean(url) })
    },
  },
]

export function registerWebMCP(): void {
  const modelContext = (navigator as unknown as { modelContext?: { provideContext?: (ctx: { tools: ToolDefinition[] }) => void } })
    .modelContext

  if (!modelContext?.provideContext) return

  try {
    modelContext.provideContext({ tools })
  } catch {
    // WebMCP is still experimental; never let registration break the page.
  }
}
